import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import oidcAuth from '../services/auth/oidc-client';
import { loginSuccess, loginFailure, logout as logoutAction } from '../store/authSlice';
import type { RootState } from '../store';
import { getRealmRolesFromToken } from '../utils/tokenRoles';

const normalizeBasePath = (value?: string | null): string => {
  if (!value || value === '/' || value === './') {
    return '';
  }
  const trimmed = value.replace(/^\/+|\/+$/g, '');
  return trimmed ? `/${trimmed}` : '';
};

const BASE_PATH = normalizeBasePath(import.meta.env.BASE_URL as string | undefined);
const loginPath = `${BASE_PATH}/login`.replace('//', '/');

interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  login: (returnUrl?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  getAccessToken: () => Promise<string | null>;
}

export const useAuth = (): UseAuthReturn => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, user } = useSelector((state: RootState) => (state as any).auth);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      const existingUser = await oidcAuth.getUser();
      if (existingUser && !existingUser.expired) {
        const profile = existingUser.profile;
        const accessToken = existingUser.access_token || '';
        const roles = getRealmRolesFromToken(accessToken);
        dispatch(
          loginSuccess({
            accessToken,
            refreshToken: existingUser.refresh_token || '',
            role: roles[0] || (profile as any).roles?.[0] || 'user',
            user: {
              id: profile.sub || '',
              username: (profile as any).preferred_username || profile.email || '',
              email: profile.email || '',
              name: (profile as any).name || '',
              roles: roles.length ? roles : (profile as any).roles || [],
            },
          }),
        );
        return true;
      }

      const authenticated = await oidcAuth.isAuthenticated();
      if (authenticated) {
        const profile = await oidcAuth.getUserProfile();
        const token = (await oidcAuth.getAccessToken()) || '';
        const roles = getRealmRolesFromToken(token);
        if (profile) {
          dispatch(
            loginSuccess({
              accessToken: token,
              refreshToken: '',
              role: roles[0] || (profile as any).roles?.[0] || 'user',
              user: {
                id: profile.sub,
                username: (profile as any).preferred_username || profile.email,
                email: profile.email,
                name: profile.name,
                roles: roles.length ? roles : (profile as any).roles || [],
              },
            }),
          );
        }
      } else {
        dispatch(loginFailure('Not authenticated'));
      }
      return authenticated;
    } catch (e) {
      console.error('Auth check error:', e);
      dispatch(loginFailure('Auth check failed'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const login = useCallback(async (returnUrl?: string): Promise<void> => {
    await oidcAuth.login(returnUrl || window.location.pathname);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      dispatch(logoutAction());
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      await oidcAuth.logout();
    } catch (e) {
      console.error('Logout error:', e);
      dispatch(logoutAction());
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = loginPath;
    }
  }, [dispatch]);

  const getAccessToken = async (): Promise<string | null> => oidcAuth.getAccessToken();

  return { isAuthenticated, isLoading, user, login, logout, checkAuth, getAccessToken };
};
